import React, { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import AdmissionApplicationModal from "./index";
import { useModal } from "@/hooks/modals/useModal";
import { transformSupplierDataToFormData } from "../SupplierMainDataList/utils/transformSupplierData";
import { getAdmissionApplicationById } from "@/apis/supplier";
import { startFlow, passFlow } from "@/apis/flow.api";
import type { StartFlowSchema } from "@/types/apis";

// ==================== 常量配置 ====================
const FLOW_CODE = "ZHUN_RU_SHEN_QING";

// ==================== 主组件 ====================
/**
 * AdmissionApplicationModal 适配器组件
 * 用于将 Modal 系统的 props 转换为 AdmissionApplicationModal 组件所需的 props
 *
 * 根据是否有 taskId 来决定显示查看模式还是编辑模式：
 * - 有 taskId: 显示查看/审批模式（从工作台点击进入）
 * - 无 taskId: 显示新增/编辑模式（从列表页点击进入）
 */
const AdmissionApplicationModalAdapter: React.FC = () => {
  const { t } = useTranslation("supplierProfile");
  const { open, props, closeModal } = useModal("ZHUN_RU_SHEN_QING");
  const {
    mode,
    viewStatus,
    recordId,
    taskId,
    isReadOnly,
    supplierName,
    supplierUsName,
  } = props || {};

  const [recordData, setRecordData] = useState<any>(null);

  // 当 modal 打开时，获取准入申请详情（仅在编辑/查看模式）
  React.useEffect(() => {
    if (open && recordId && !recordData && (mode === "inReview" || taskId)) {
      getAdmissionApplicationById(recordId)
        .then((response) => {
          if (response.data.code === 200 && response.data.data) {
            const transformedData = transformSupplierDataToFormData(
              response.data.data,
            );
            transformedData.viewStatus = viewStatus;
            setRecordData(transformedData);
          } else {
            message.error(t("获取准入申请详情失败"));
          }
        })
        .catch((error) => {
          message.error(t("获取准入申请详情失败，请重试"));
        })
        .finally(() => {});
    }
  }, [open, recordId, mode, taskId, viewStatus, t]);

  // 处理关闭
  const handleClose = useCallback(() => {
    closeModal();
    setRecordData(null);
  }, [closeModal]);

  // 处理提交
  const handleSubmit = useCallback(
    async (submitData: any, flowStatusParam: number = 1) => {
      try {
        // 验证必需的字段
        if (!submitData.processCode) {
          message.error(t("缺少流程单号，无法提交申请"));
          return;
        }

        // 判断是否为草稿模式
        const isDraftStatus =
          recordData?.admissionApplicationStatus === "草稿" ||
          recordData?.viewStatus === "草稿";

        if (isDraftStatus && taskId) {
          // 草稿模式：调用 /view/pass 接口
          const passFlowData = {
            flowCode: FLOW_CODE,
            formNo: submitData.processCode,
            variable: {
              supplierUserId: recordData?.supplierUserId || recordData?.id,
            },
            srmSupplierAdmissionApplicationDTO: submitData,
          };

          const response = await passFlow(taskId, passFlowData);

          if (response?.data?.code === 200 || response?.data === undefined) {
            message.success(t("申请已通过，请等待下一步"));
            handleClose();
          } else {
            message.error(response?.data?.message || t("申请提交失败"));
          }
        } else {
          // 非草稿模式：调用 /view/start 接口
          const flowParams: StartFlowSchema = {
            flowCode: FLOW_CODE,
            flowStatus: flowStatusParam,
            formNo: submitData.processCode,
            variable: {
              supplierUserId: recordData?.supplierUserId || recordData?.id,
            },
            srmSupplierAdmissionApplicationDTO: submitData,
          };

          const response = await startFlow(flowParams);

          if (response?.data?.code === 200 || response?.data === undefined) {
            const message_text =
              flowStatusParam === 0
                ? t("申请已暂存，待需要时继续提交")
                : t("申请已提交，请等待下一步");
            message.success(message_text);
            handleClose();
          } else {
            message.error(response?.data?.message || t("申请提交失败"));
          }
        }
      } catch (error) {
        message.error(t("提交准入申请失败，请重试"));
      }
    },
    [recordData, taskId, handleClose, t],
  );

  // 判断是否为只读模式
  const isReadOnlyMode = viewStatus === true || isReadOnly;

  // 在新建模式下，recordId 是 supplierCode
  // 在编辑/查看模式下，recordId 是 applicationId
  const isNewMode = mode === "started" && !taskId;
  const applicationId = !isNewMode ? recordId : undefined;
  console.log("applicationId", applicationId);
  console.log("recordData", recordData);
  // 在新建模式下，创建一个包含供应商信息的对象供 AdmissionApplicationModal 使用
  const newModeRecordData = isNewMode
    ? {
        supplierCode: recordId,
        supplierName,
        supplierUsName,
      }
    : null;

  return (
    <AdmissionApplicationModal
      visible={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      recordData={applicationId ? recordData : newModeRecordData}
      readOnly={isReadOnlyMode}
      isApplyChangeMode={false}
      isResubmitMode={false}
      isFromWorkbench={!!taskId}
    />
  );
};

export default AdmissionApplicationModalAdapter;
